/* eslint-disable max-lines */
import { cloneDeep, isEqual, merge } from "lodash";
import { useSelector } from "react-redux";
import { useRequest } from "redux-query-react";
import { getConfig } from "plugins/config-loader";
import { DashboardTypes } from "./types";
import { analysisSelector, pathInformationSelector, useCurrentDashboardEntityId, useCurrentDashboardTemplate, useCurrentDashboardType, useGetSelectedDashboardOptions, useGetSelectedReferenceDate } from "./use-current-dashboard";
import { DatapointIdStandardEnum, DatapointsEntityType, postDatapointsFilters, useGetDatapointsMetadata } from "api/ux/src";
import { getDateWhenString } from "common/date-utils";
import { objectToHash } from "common/object-helper";
import { AuxFacetedFilterOptionTypeEnum } from "components/ux/ins-faceted-filter/types";
import { usePatchedWidgetComponentProps } from "components/widgets/hooks/use-current-widget";
import { FilterDataTypeEnum } from "hooks/widget-data/types";
import { getDatapointIdFromIdString, getDatapointLabelForDatapointId, globalQueryStateSelector, useCurrentDashboardAnalysisFilters, useCurrentDashboardLabelOverrides, useCurrentDashboardSectionOrder, useGetCustomDatapointDimensionsByPortfolioCreationChoice } from "hooks/widget-data/use-datapoints";
import { useCurrentWidgetFilterValues } from "store/current-dashboard/selectors";
import { PortfolioCreationChoice } from "components/modal/portfolio-creation-edit/types";
const DEFAULT_NUMBER_RANGE_MAX = 100;
const DEFAULT_NUMBER_RANGE_MIN = 0;
const DEFAULT_STEP_INT = 1;
const DEFAULT_STEP_FLOAT = 0.01;
const dashboardTypetoEntityType = (type) => {
    if (type === DashboardTypes.Asset) {
        return DatapointsEntityType.Asset;
    }
    if (type === DashboardTypes.Fund) {
        return DatapointsEntityType.Fund;
    }
    if (type === DashboardTypes.FundSeries) {
        return DatapointsEntityType.FundSeries;
    }
    if (type === DashboardTypes.Manager) {
        return DatapointsEntityType.Manager;
    }
    if (type === DashboardTypes.Portfolio) {
        return DatapointsEntityType.Portfolio;
    }
    if (type === DashboardTypes.Market) {
        return DatapointsEntityType.Market;
    }
    return null;
};
const createRequestParameters = (requestOptions) => {
    const requestParameters = {
        datapointsFiltersRequest: {
            asOfDate: requestOptions.asOfDate,
            dashboardOptions: requestOptions.dashboardOptions || {},
            entityId: requestOptions.entityId,
            entityType: requestOptions.entityType,
            filters: requestOptions.filters?.map((x) => ({
                datapoint: getDatapointIdFromIdString(x),
                // To get from metadata afterwards
                id: x
            })) || []
        }
    };
    if (requestParameters.datapointsFiltersRequest) {
        requestParameters.datapointsFiltersRequest.id = objectToHash(requestParameters);
    }
    return requestParameters;
};
const useCreateRequest = () => {
    const asOfDate = getDateWhenString(useGetSelectedReferenceDate()), currentDashboardType = useCurrentDashboardType(), dashboardOptions = useGetSelectedDashboardOptions(), entityId = useCurrentDashboardEntityId(), entityType = dashboardTypetoEntityType(currentDashboardType), filters = useCurrentDashboardAnalysisFilters();
    if (currentDashboardType === DashboardTypes.Asset) {
        return null;
    }
    if (!asOfDate || !entityType || !entityId) {
        return null;
    }
    const requestOptions = {
        asOfDate,
        dashboardOptions,
        entityId,
        entityType,
        filters
    };
    return createRequestParameters(requestOptions);
};
const usePortfolioCreationCreateRequest = () => {
    const customFields = useGetCustomDatapointDimensionsByPortfolioCreationChoice(PortfolioCreationChoice.CreatePortfolioRuleBased)
        .filter((x) => {
        if (!x.metadata || typeof x.metadata !== "object") {
            return false;
        }
        const metadata = x.metadata;
        return metadata.format === FilterDataTypeEnum.String || metadata.format === FilterDataTypeEnum.Boolean;
    });
    const date = new Date();
    date.setHours(0, 0, 0, 0);
    const asOfDate = date, dashboardOptions = {}, entityId = "All", entityType = DatapointsEntityType.Portfolio, filters = [
        DatapointIdStandardEnum.FundStrategy,
        DatapointIdStandardEnum.FundVintageYear,
        DatapointIdStandardEnum.FundGeography,
        DatapointIdStandardEnum.FundInvestorName,
        DatapointIdStandardEnum.FundAssetClass,
        DatapointIdStandardEnum.FundCurrency,
        DatapointIdStandardEnum.FundManagerId
    ];
    const customFieldIds = customFields.map((cf) => cf.id).filter(Boolean);
    const allFilters = [...filters, ...customFieldIds];
    const requestOptions = {
        asOfDate,
        dashboardOptions,
        entityId,
        entityType,
        filters: allFilters
    };
    return createRequestParameters(requestOptions);
};
const useMarketCreationCreateRequest = () => {
    const date = new Date();
    date.setHours(0, 0, 0, 0);
    const asOfDate = date, dashboardOptions = {}, entityId = "All", entityType = DatapointsEntityType.Market, filters = [
        DatapointIdStandardEnum.FundAssetClass,
        DatapointIdStandardEnum.FundStrategy,
        DatapointIdStandardEnum.FundGeography,
        DatapointIdStandardEnum.FundVintageYear,
        DatapointIdStandardEnum.FundCurrency
    ];
    const requestOptions = {
        asOfDate,
        dashboardOptions,
        entityId,
        entityType,
        filters
    };
    return createRequestParameters(requestOptions);
};
export const getFilterData = (id, metadata, labelOverrides) => {
    const labels = metadata.toMap((m) => m.id ?? "", (m) => ({ group: m.metadata?.path?.[0] ?? "", label: m.metadata?.label?.toString() ?? "", type: m.metadata?.format?.toString() ?? "" }));
    const key = getDatapointLabelForDatapointId(id);
    const baseData = labels.get(key) || { group: "", label: "", type: "" };
    const override = labelOverrides?.find((o) => o.datapointId === id);
    if (override) {
        return {
            group: override.group ?? baseData.group,
            label: override.label ?? baseData.label,
            type: override.type ?? baseData.type
        };
    }
    return baseData;
};
const getFilterSection = (sections, key) => {
    let section = sections.find((x) => x.group === key);
    if (!section) {
        section = { data: [], group: key, label: `by ${key}` };
        sections.push(section);
    }
    return section;
};
const defaultSectionRank = (group) => {
    if (group === "Fund") {
        return 0;
    }
    if (group === "Investor Account") {
        return 1;
    }
    return 2;
};
const sortFilterSections = (sections, sectionOrder) => {
    if (sectionOrder.length === 0) {
        sections.sort((a, b) => {
            const rankA = defaultSectionRank(a.group);
            const rankB = defaultSectionRank(b.group);
            if (rankA !== rankB) {
                return rankA - rankB;
            }
            return a.label.localeCompare(b.label);
        });
        return;
    }
    sections.sort((a, b) => {
        const orderA = sectionOrder.indexOf(a.group);
        const orderB = sectionOrder.indexOf(b.group);
        const rankA = orderA === -1 ? 100 : orderA;
        const rankB = orderB === -1 ? 100 : orderB;
        if (rankA !== rankB) {
            return rankA - rankB;
        }
        return a.label.localeCompare(b.label);
    });
};
const shouldIncludeNumberRangeFilter = (type, filter) => type === FilterDataTypeEnum.Value ||
    type === FilterDataTypeEnum.Multiple ||
    type === FilterDataTypeEnum.Amount ||
    (filter.minNumberValue !== filter.maxNumberValue);
const mapPickListData = (values) => {
    const groupedByParent = values.reduce((acc, item) => {
        const parentCode = item.parentCode || "";
        if (!acc[parentCode]) {
            acc[parentCode] = [];
        }
        acc[parentCode].push(item);
        return acc;
    }, {});
    const buildTree = (parentCode, visited = new Set()) => (groupedByParent[parentCode] || []).map((item) => {
        // Prevent infinite loop: do not recurse if code equals parentCode or already visited
        if (item.code === parentCode || visited.has(item.code)) {
            return {
                children: undefined,
                isChecked: false,
                isExpanded: false,
                label: item.label,
                uid: item.code
            };
        }
        const newVisited = new Set(visited);
        newVisited.add(item.code);
        const children = buildTree(item.code, newVisited);
        return {
            children: children && children.length > 0 ? children : undefined,
            isChecked: false,
            isExpanded: false,
            label: item.label,
            uid: item.code
        };
    });
    return buildTree("");
};
const createDateRangeValue = (id, label, values) => {
    const dateValues = values[id];
    const result = {
        field: id,
        label,
        type: AuxFacetedFilterOptionTypeEnum.DateRange
    };
    if (dateValues && !Array.isArray(dateValues) && dateValues.type === "date") {
        result.startDateValue = dateValues.startDate;
        result.endDateValue = dateValues.endDate;
    }
    return result;
};
const createNumberRangeValue = (id, label, filter, values, type) => {
    // Asset count is an integer but comes trough as a type value
    const step = type === FilterDataTypeEnum.String || id === "Asset.Count" ? DEFAULT_STEP_INT : DEFAULT_STEP_FLOAT;
    const result = {
        field: id,
        fixedDecimal: step === 1 ? 0 : 2,
        label,
        maxValue: filter.maxNumberValue ?? DEFAULT_NUMBER_RANGE_MAX,
        minValue: filter.minNumberValue ?? DEFAULT_NUMBER_RANGE_MIN,
        step,
        type: AuxFacetedFilterOptionTypeEnum.MultiSlider
    };
    return result;
};
const createPickListValue = (id, label, filter) => ({
    data: mapPickListData(filter.values || []),
    field: id,
    label,
    type: "picklist",
    uid: id
});
const createMultiSelectValue = (id, label, filter, values) => {
    const stringValues = filter?.values || [];
    const entriesFilter = {};
    stringValues.forEach((f) => {
        if (f.code && f.label) {
            const entryFilter = entriesFilter[f.label];
            if (entryFilter) {
                entryFilter.codes.push(f.code);
            }
            else {
                entriesFilter[f.label] = {
                    checked: values[id]?.some((s) => s === f.code) || false,
                    codes: [f.code || ""],
                    label: f.label || "",
                    parentCode: f.parentCode || ""
                };
            }
        }
    });
    return {
        field: id,
        label,
        type: AuxFacetedFilterOptionTypeEnum.MultiSelect,
        values: Object.values(entriesFilter) || []
    };
};
const createFilterValue = (id, label, filter, type, values) => {
    const isDateFilter = type === FilterDataTypeEnum.Date;
    if (isDateFilter) {
        return createDateRangeValue(id, label, values);
    }
    const isNumberRangeFilter = shouldIncludeNumberRangeFilter(type, filter);
    if (isNumberRangeFilter) {
        return createNumberRangeValue(id, label, filter, values, type);
    }
    const stringValues = filter?.values || [];
    const hasParentCode = Object.values(stringValues).some((entry) => entry.parentCode);
    if (hasParentCode) {
        return createPickListValue(id, label, filter);
    }
    return createMultiSelectValue(id, label, filter, values);
};
const shouldIncludeFilter = (filter, type) => {
    const stringValues = filter?.values || [];
    return shouldIncludeNumberRangeFilter(type, filter) || stringValues.length > 0;
};
const resolveFilterGroup = (id, group) => {
    const datapointKey = getDatapointLabelForDatapointId(id);
    if (datapointKey === DatapointIdStandardEnum.FundInvestorName) {
        return "Investor Account";
    }
    return group;
};
// eslint-disable-next-line max-statements
const createFilterValues = (filters, values, metadata, labelOverrides, sectionOrder = []) => {
    const result = [];
    for (const filter of filters) {
        const id = filter.id || "";
        const { label, group, type } = getFilterData(id, metadata, labelOverrides);
        if (shouldIncludeFilter(filter, type)) {
            const filterGroup = resolveFilterGroup(id, group);
            const section = getFilterSection(result, filterGroup);
            section.data.push(createFilterValue(id, label, filter, type, values));
        }
    }
    sortFilterSections(result, sectionOrder);
    return result;
};
const useGetSortedFilters = (dataKey, items) => {
    const filters = cloneDeep(useSelector((state) => state.entities.DatapointsFilters[dataKey]?.filters)) || [];
    const definitions = useCurrentDashboardAnalysisFilters();
    const ids = definitions.map((d) => {
        const item = items.find((i) => i.datapoint.standard === d || i.datapoint.custom === d);
        return item?.id;
    });
    return filters.sort((a, b) => ids.indexOf(a.id) - ids.indexOf(b.id));
};
const useCurrentFilterValues = () => {
    const analysisFilters = useCurrentDashboardAnalysisFilters();
    const allFilters = useSelector((state) => state.currentDashboard.currentFilterValues || {}, isEqual);
    return analysisFilters.toRecord((f) => f, (f) => allFilters[f]);
};
const useCurrentAnalysis = () => {
    const { currentTemplate, currentSection } = useSelector((state) => {
        const { selectedSection: requestedSection, selectedType } = pathInformationSelector(state);
        return {
            currentSection: requestedSection,
            currentTemplate: analysisSelector(selectedType)(state)
        };
    }, isEqual);
    for (const analysis of currentTemplate.analysis ?? []) {
        const targetSection = analysis.sections?.filter((s) => s.id === currentSection)[0];
        if (targetSection && targetSection.id) {
            return analysis;
        }
    }
    return undefined;
};
const useDefaultFilterValues = () => {
    const newAnalysis = useCurrentAnalysis();
    const template = useCurrentDashboardTemplate();
    const sourceDefaults = newAnalysis?.defaultFilters ?? template?.defaultFilters;
    if (sourceDefaults) {
        const defaultFilters = {};
        sourceDefaults.forEach((filter) => {
            if (filter.key) {
                defaultFilters[filter.key] = filter.value;
            }
        });
        return defaultFilters;
    }
    return undefined;
};
export const useCurrentAnalysisAutoMode = () => {
    const template = useCurrentDashboardTemplate();
    return template?.applyFiltersOnChange ?? false;
};
export const smartFilteringEnabled = String(getConfig("REACT_APP_SMART_FILTERING_ENABLED")) === "true";
export const useSmartFilterConfig = () => {
    const template = useCurrentDashboardTemplate();
    return template?.smartFilterConfig;
};
export const useFilterValues = (withoutDefaultFilter) => {
    const activeFilterValues = useCurrentFilterValues();
    const defaultFilterValues = useDefaultFilterValues();
    if (withoutDefaultFilter) {
        return activeFilterValues;
    }
    const aggregateFilters = merge(activeFilterValues, defaultFilterValues);
    return aggregateFilters;
};
export const useGetDatapointsFilters = (withoutDefaultFilter) => {
    const requestParameters = useCreateRequest();
    const dataKey = requestParameters?.datapointsFiltersRequest?.id || "";
    const request = requestParameters ? postDatapointsFilters(requestParameters, { queryKey: `PostFiltersForDatapoints-${dataKey}` }) : null;
    const [, ForceRefresh] = useRequest(request);
    const globalQueryState = useSelector((state) => globalQueryStateSelector(state, "PostFiltersForDatapoints", dataKey), isEqual);
    const filters = useGetSortedFilters(dataKey, requestParameters?.datapointsFiltersRequest?.filters || []);
    const values = useFilterValues(withoutDefaultFilter);
    const [metadata] = useGetDatapointsMetadata();
    const labelOverrides = useCurrentDashboardLabelOverrides();
    const sectionOrder = useCurrentDashboardSectionOrder();
    return [createFilterValues(filters, values, metadata, labelOverrides, sectionOrder), globalQueryState, ForceRefresh];
};
export const useGetDefaultWidgetFilters = (defaultWidgetFilters) => {
    if (Object.keys(defaultWidgetFilters).length === 0) {
        return {};
    }
    return Object.entries(defaultWidgetFilters).reduce((acc, [key, value]) => {
        if (value.length > 0) {
            acc[key] = value;
        }
        return acc;
    }, {});
};
export const useWidgetFilterValues = (sectionId, widgetId) => {
    const componentProps = usePatchedWidgetComponentProps(sectionId, widgetId);
    const childWidgetFilterValues = useCurrentWidgetFilterValues(widgetId) ?? {}, parentActiveWidgetFilterValues = useCurrentWidgetFilterValues(componentProps?.parentId) ?? {};
    const activeWidgetFilterValues = Object.keys(parentActiveWidgetFilterValues)?.length ? parentActiveWidgetFilterValues : childWidgetFilterValues;
    const defaultWidgetFilterValues = useGetDefaultWidgetFilters(componentProps?.defaultFilters ?? {});
    return Object.keys(defaultWidgetFilterValues).length === 0 ? activeWidgetFilterValues : merge(activeWidgetFilterValues, defaultWidgetFilterValues);
};
export const useGetDatapointsPortfolioCreationFilters = () => {
    const requestParameters = usePortfolioCreationCreateRequest();
    const dataKey = requestParameters?.datapointsFiltersRequest?.id || "";
    const request = requestParameters ? postDatapointsFilters(requestParameters, { queryKey: `PostFiltersForDatapointsPortfolioCreation-${dataKey}` }) : null;
    const [, ForceRefresh] = useRequest(request);
    const globalQueryState = useSelector((state) => globalQueryStateSelector(state, "PostFiltersForDatapointsPortfolioCreation", dataKey), isEqual);
    const filters = useGetSortedFilters(dataKey, requestParameters?.datapointsFiltersRequest?.filters || []);
    const values = useFilterValues();
    const [metadata] = useGetDatapointsMetadata();
    return [createFilterValues(filters, values, metadata), globalQueryState, ForceRefresh];
};
export const useGetDatapointsMarketCreationFilters = () => {
    const requestParameters = useMarketCreationCreateRequest();
    const dataKey = requestParameters?.datapointsFiltersRequest?.id || "";
    const request = requestParameters ? postDatapointsFilters(requestParameters, { queryKey: `PostFiltersForDatapointsMarketCreation-${dataKey}` }) : null;
    const [, ForceRefresh] = useRequest(request);
    const globalQueryState = useSelector((state) => globalQueryStateSelector(state, "PostFiltersForDatapointsMarketCreation", dataKey), isEqual);
    const filters = useGetSortedFilters(dataKey, requestParameters?.datapointsFiltersRequest?.filters || []);
    const values = useFilterValues();
    const [metadata] = useGetDatapointsMetadata();
    return [createFilterValues(filters, values, metadata), globalQueryState, ForceRefresh];
};
const useFundOnlyPortfolioCreationCreateRequest = (customFields = []) => {
    const asOfDate = new Date(new Date().setHours(0, 0, 0, 0)), dashboardOptions = {}, entityId = "All", entityType = DatapointsEntityType.Portfolio, filters = [
        DatapointIdStandardEnum.FundStrategy,
        DatapointIdStandardEnum.FundVintageYear,
        DatapointIdStandardEnum.FundGeography,
        DatapointIdStandardEnum.FundAssetClass,
        DatapointIdStandardEnum.FundCurrency,
        DatapointIdStandardEnum.FundManagerId
    ];
    const customFieldIds = customFields.map((cf) => cf.id).filter(Boolean);
    const allFilters = [...filters, ...customFieldIds];
    const requestOptions = {
        asOfDate,
        dashboardOptions,
        entityId,
        entityType,
        filters: allFilters
    };
    return createRequestParameters(requestOptions);
};
export const useGetDatapointsFundOnlyFilters = () => {
    const customFields = useGetCustomDatapointDimensionsByPortfolioCreationChoice(PortfolioCreationChoice.CreatePortfolioRuleBasedFunds)
        .filter((x) => {
        if (!x.metadata || typeof x.metadata !== "object") {
            return false;
        }
        const metadata = x.metadata;
        return metadata.format === FilterDataTypeEnum.String || metadata.format === FilterDataTypeEnum.Boolean;
    });
    const requestParameters = useFundOnlyPortfolioCreationCreateRequest(customFields);
    const dataKey = requestParameters?.datapointsFiltersRequest?.id || "";
    const request = requestParameters ? postDatapointsFilters(requestParameters, { queryKey: `PostFiltersForDatapointsFundOnlyCreation-${dataKey}` }) : null;
    const [, ForceRefresh] = useRequest(request);
    const globalQueryState = useSelector((state) => globalQueryStateSelector(state, "PostFiltersForDatapointsFundOnlyCreation", dataKey), isEqual);
    const filters = useGetSortedFilters(dataKey, requestParameters?.datapointsFiltersRequest?.filters || []);
    const values = useFilterValues();
    const [metadata] = useGetDatapointsMetadata();
    return [createFilterValues(filters, values, metadata), globalQueryState, ForceRefresh];
};
